import React, { useState } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../utils/config";
import {
  FiCalendar, FiClock, FiCheck, FiX, FiInbox,
  FiSearch, FiVideo, FiMapPin, FiBriefcase, FiInfo, FiRefreshCw
} from "react-icons/fi";
import "./EmployeeDashboard.css";

const Interview = () => {
  const { appliedJobs, searchQuery: globalSearchQuery, fetchDashboardData } = useOutletContext();

  const [localSearchQuery, setLocalSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [confirmingId, setConfirmingId] = useState(null);

  const interviewApps = appliedJobs.filter(app =>
    app.interviewStatus === "Invited" || app.interviewStatus === "Rescheduled"
  );

  const statuses = ["Pending", "Confirmed", "Declined"];

  const filteredApps = interviewApps.filter(app => {
    const combinedQuery = (globalSearchQuery || localSearchQuery).toLowerCase();
    const matchesSearch =
      (app.jobId?.role || app.role || "").toLowerCase().includes(combinedQuery) ||
      (app.candidateInterviewStatus || "").toLowerCase().includes(combinedQuery);

    const matchesStatus = statusFilter
      ? (app.candidateInterviewStatus || "Pending") === statusFilter
      : true;

    let matchesDate = true;
    if (dateFilter && app.interviewTime) {
      const schedDate = new Date(app.interviewTime).toISOString().split("T")[0];
      matchesDate = schedDate === dateFilter;
    }

    return matchesSearch && matchesStatus && matchesDate;
  });

  const handleConfirm = async (applicationId, status, note = "") => {
    setConfirmingId(applicationId + status);
    try {
      const token = localStorage.getItem("candidateToken");
      await axios.put(
        `${API_BASE_URL}/candidate/confirm-interview`,
        { applicationId, status, note },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchDashboardData();
    } catch (err) {
      console.error("Error updating interview status:", err);
      alert("Failed to update status. Please try again.");
    } finally {
      setConfirmingId(null);
    }
  };

  const resetFilters = () => {
    setLocalSearchQuery("");
    setStatusFilter("");
    setDateFilter("");
  };

  return (
    <div className="emp-dash">
      <div className="p-4 sm:p-6 lg:p-8">
        {/* Dashboard Header */}
        <div className="emp-dash__header">
          <div>
            <h1 className="emp-dash__greeting">
              Interview <span>Schedule</span>
            </h1>
            <p className="emp-dash__subtitle">
              Manage your interview calendar
            </p>
          </div>
          <div className="emp-dash__date-pill">
            <FiVideo />
            <span>
              {new Date().toLocaleDateString("en-US", {
                weekday: "short",
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </span>
          </div>
        </div>

        {/* Filter Section */}
        <div className="emp-dash__card mb-6">
          <div className="emp-dash__card-header">
            <div>
              <h3 className="emp-dash__card-title flex items-center gap-2">
                <FiSearch className="text-indigo-600" /> Filter Interviews
              </h3>
              <p className="emp-dash__card-desc">Search by role, status, or date</p>
            </div>
          </div>
          <div className="emp-dash__card-body bg-gray-50/50">
            <div className="flex flex-wrap items-center gap-3">
              {/* Search */}
              <div className="relative flex-1 min-w-[200px]">
                <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search interviews..."
                  value={localSearchQuery}
                  onChange={(e) => setLocalSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 text-sm border border-gray-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent shadow-sm transition-all duration-200 hover:border-gray-300 bg-white"
                />
              </div>

              {/* Status Filter */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="h-11 px-4 text-sm font-semibold border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white shadow-sm transition-all duration-200 hover:border-gray-300"
              >
                <option value="">All Statuses</option>
                {statuses.map(s => <option key={s} value={s}>{s}</option>)}
              </select>

              {/* Date Filter */}
              <input
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="h-11 px-4 text-sm font-semibold border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white shadow-sm transition-all duration-200 hover:border-gray-300"
              />

              {/* Reset */}
              {(localSearchQuery || statusFilter || dateFilter) && (
                <button
                  onClick={resetFilters}
                  className="h-11 px-5 text-sm font-semibold text-gray-700 bg-white border-2 border-red-200 rounded-xl hover:bg-red-50 hover:border-red-300 transition-all duration-200 shadow-md flex items-center gap-2"
                >
                  <FiRefreshCw className="text-sm" /> Clear Filters
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div className="emp-dash__card py-6">
          <div className="emp-dash__card-header">
            <div>
              <h3 className="emp-dash__card-title flex items-center gap-2">
                <FiBriefcase className="text-indigo-600" /> Interview Schedule
              </h3>
              <p className="emp-dash__card-desc">Showing {filteredApps.length} interviews</p>
            </div>
          </div>
          {filteredApps.length > 0 ? (
            <div className="emp-dash__table-wrap overflow-x-auto !block">
              <table className="emp-dash__table min-w-[800px]">
                <thead>
                  <tr>
                    <th className="py-3 px-4 text-center font-bold">Job Position</th>
                    <th className="py-3 px-4 text-center font-bold">Interview Time</th>
                    <th className="py-3 px-4 text-center font-bold">Mode</th>
                    <th className="py-3 px-4 text-center font-bold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredApps.map((app) => (
                    <tr key={app._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-2 text-center">
                        <div className="text-xs font-bold text-gray-800">{app.jobId?.role || "Position Data"}</div>
                        <div className="text-[9px] text-gray-400 font-bold uppercase tracking-wider mt-0.5">Ref: {app._id.slice(-6).toUpperCase()}</div>
                      </td>
                      <td className="px-4 py-2 text-center">
                        <div className="inline-flex items-center justify-center gap-1.5 px-3 py-1 bg-gray-50 rounded-lg border border-gray-100 text-[10px] font-bold text-gray-600">
                          <FiClock className="text-blue-500" />
                          {new Date(app.interviewTime).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-center">
                        <div className="flex flex-col items-center justify-center gap-1">
                          <span className={`text-[9px] font-bold px-2 py-0.5 rounded uppercase tracking-wider flex items-center gap-1 ${app.interviewMode === "online" ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}`}>
                             {app.interviewMode === "online" ? <FiVideo className="text-[8px]"/> : <FiMapPin className="text-[8px]"/>}
                            {app.interviewMode}
                          </span>
                          {app.interviewMode === "online" && app.interviewMeetLink && (
                            <a
                              href={app.interviewMeetLink}
                              target="_blank"
                              rel="noreferrer"
                              className="text-[9px] font-bold text-blue-500 hover:text-blue-700 underline uppercase mt-0.5 block tracking-widest"
                            >
                              Join Meeting
                            </a>
                          )}
                          {app.interviewMode === "offline" && app.interviewAddress && (
                            <span className="text-[8px] font-bold text-gray-400 uppercase tracking-widest max-w-[120px] truncate block mt-0.5" title={app.interviewAddress}>
                              {app.interviewAddress}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-center">
                        <div className="flex flex-col items-center justify-center gap-1">
                          {(!app.candidateInterviewStatus || app.candidateInterviewStatus === "Pending") ? (
                            <div className="flex gap-4">
                              <button
                                onClick={() => handleConfirm(app._id, "Confirmed")}
                                disabled={!!confirmingId}
                                className="text-emerald-600 hover:text-emerald-800 transition-all active:scale-90 disabled:opacity-30"
                                title="Accept"
                              >
                                <FiCheck size={18} />
                              </button>
                              <button
                                onClick={() => {
                                  const reason = prompt("Optional: Provide a reason or request an alternate time:");
                                  if (reason !== null) handleConfirm(app._id, "Declined", reason);
                                }}
                                disabled={!!confirmingId}
                                className="text-rose-600 hover:text-rose-800 transition-all active:scale-90 disabled:opacity-30"
                                title="Decline/Reschedule"
                              >
                                <FiX size={18} />
                              </button>
                            </div>
                          ) : (
                            <span className={`text-[9px] px-2 py-0.5 font-bold uppercase tracking-widest rounded ${app.candidateInterviewStatus === "Confirmed" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                              {app.candidateInterviewStatus}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="py-10 text-center">
              <div className="flex flex-col items-center justify-center gap-3">
                <div className="emp-dash__spinner"></div>
                <span className="text-sm font-medium text-gray-500">No interviews scheduled</span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-center gap-2 text-[9px] font-bold text-gray-400 uppercase tracking-widest py-4">
          <FiInfo className="text-indigo-400" />
          Timely Health Interview Scheduler • {new Date().getFullYear()}
        </div>
      </div>
    </div>
  );
};

export default Interview;